import React, { useState, useEffect } from 'react';
import { organizationsAPI, usersAPI, clustersAPI, serversAPI, gpuAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ALL_SECTIONS, ACTIONS, sectionLabel, sectionCovers } from '../constants/sectionLabels';
import ActionsMenu, { MENU_ITEM } from '../components/ActionsMenu';

const PLAN_BADGE = {
  free:       { bg: 'var(--border)', color: 'var(--ink-2)', label: 'Free' },
  pro:        { bg: 'var(--brand-soft)', color: 'var(--brand)', label: 'Pro' },
  enterprise: { bg: 'var(--color-warning-50)', color: 'var(--color-warning-700)', label: 'Enterprise' },
};

function PlanBadge({ plan }) {
  const b = PLAN_BADGE[plan] || PLAN_BADGE.free;
  return (
    <span className="badge" style={{ background: b.bg, color: b.color }}>
      {b.label}
    </span>
  );
}

export default function OrgManagement() {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const [orgs, setOrgs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null); // org detail view
  const [orgStats, setOrgStats]   = useState({});
  const [orgUsers, setOrgUsers]   = useState([]);
  const [allClusters, setAllClusters] = useState([]);
  const [allServers, setAllServers]   = useState([]);
  const [assignClusterId, setAssignClusterId] = useState('');  // cluster picker in org detail
  const [orgSections, setOrgSections] = useState([]);          // enabled sections for selected org
  const [orgSectionActions, setOrgSectionActions] = useState({}); // { section: [view,create,edit,delete] }
  const [savingSections, setSavingSections] = useState(false);
  const [orgGpuServers, setOrgGpuServers] = useState([]);      // all registered GPU servers
  const [orgGpuAssigned, setOrgGpuAssigned] = useState([]);    // gpu server ids this org may access
  const [savingGpu, setSavingGpu] = useState(false);
  const [allAllocations, setAllAllocations] = useState([]);    // every GPU/CPU instance, any org
  const [assignAllocId, setAssignAllocId] = useState('');      // instance picker in org detail

  // Create org modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', slug: '', description: '', plan: 'free', max_servers: '', max_clusters: '' });
  const [createError, setCreateError] = useState('');

  // Create user in org modal
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [userForm, setUserForm] = useState({ username: '', email: '', password: '', full_name: '', phone_number: '', rbac_role: 'member' });
  const [userError, setUserError] = useState('');
  const [showCreateUserPassword, setShowCreateUserPassword] = useState(false);
  const [editUserPermsPassword, setEditUserPermsPassword] = useState('');
  const [showEditUserPermsPassword, setShowEditUserPermsPassword] = useState(false);

  // Edit org modal
  const [editOrg, setEditOrg] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState('');

  // Edit user permissions modal
  const [editUserPerms, setEditUserPerms] = useState(null); // user object
  const [editRole, setEditRole]           = useState('');
  const [editSectionActions, setEditSectionActions] = useState({}); // {section:[verbs]} per-verb member grant
  const [editClusters, setEditClusters]   = useState([]);
  const [editServers, setEditServers]     = useState([]);
  const [editPermsError, setEditPermsError] = useState('');
  const [editPermsLoading, setEditPermsLoading] = useState(false);

  const ROLES = ['admin','member'];

  useEffect(() => {
    if (!isSuperAdmin) { navigate('/'); return; }
    load();
  }, [isSuperAdmin, navigate]);

  async function load() {
    setLoading(true);
    try {
      const [oRes, cRes, sRes, aRes] = await Promise.all([
        organizationsAPI.list(),
        clustersAPI.list(),
        serversAPI.list(),
        gpuAPI.allAllocations(),
      ]);
      setOrgs(oRes.data);
      setAllClusters(cRes.data || []);
      setAllServers(sRes.data || []);
      setAllAllocations(aRes.data || []);
    } catch (e) {
      setError('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }

  async function selectOrg(org) {
    setSelectedOrg(org);
    setAssignClusterId('');
    setAssignAllocId('');
    try {
      const [statsRes, usersRes, secRes, gpuRes] = await Promise.all([
        organizationsAPI.stats(org.id),
        usersAPI.list(org.id),
        organizationsAPI.getSections(org.id),
        organizationsAPI.getGpuServers(org.id),
      ]);
      setOrgStats(statsRes.data);
      setOrgUsers(usersRes.data);
      setOrgSections(secRes.data.enabled_sections || []);
      setOrgSectionActions(secRes.data.section_actions || {});
      setOrgGpuServers(gpuRes.data.all_servers || []);
      setOrgGpuAssigned(gpuRes.data.assigned_ids || []);
    } catch (e) {}
  }

  function toggleOrgSection(section) {
    setOrgSections(prev =>
      prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]
    );
    setOrgSectionActions(prev => {
      const next = { ...prev };
      if (next[section]) delete next[section];               // disabling — drop all actions
      else next[section] = ['view', 'create', 'edit', 'delete']; // enabling — grant all by default
      return next;
    });
  }

  function toggleSectionAction(section, action) {
    if (action === 'view') return;                           // view is always included
    setOrgSectionActions(prev => {
      const cur = prev[section] || ['view'];
      const nextActs = cur.includes(action) ? cur.filter(a => a !== action) : [...cur, action];
      if (!nextActs.includes('view')) nextActs.unshift('view');
      return { ...prev, [section]: nextActs };
    });
  }

  async function handleSaveOrgSections() {
    if (!selectedOrg) return;
    setSavingSections(true);
    try {
      const section_actions = {};
      orgSections.forEach(sec => { section_actions[sec] = orgSectionActions[sec] || ['view']; });
      await organizationsAPI.setSections(selectedOrg.id, { section_actions });
      const secRes = await organizationsAPI.getSections(selectedOrg.id);
      setOrgSections(secRes.data.enabled_sections || []);
      setOrgSectionActions(secRes.data.section_actions || {});
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save sections');
    } finally {
      setSavingSections(false);
    }
  }

  function toggleGpuServer(id) {
    setOrgGpuAssigned(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  async function handleSaveGpuServers() {
    if (!selectedOrg) return;
    setSavingGpu(true);
    try {
      await organizationsAPI.setGpuServers(selectedOrg.id, orgGpuAssigned);
      const gpuRes = await organizationsAPI.getGpuServers(selectedOrg.id);
      setOrgGpuServers(gpuRes.data.all_servers || []);
      setOrgGpuAssigned(gpuRes.data.assigned_ids || []);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save GPU access');
    } finally {
      setSavingGpu(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setCreateError('');
    try {
      const payload = {
        ...createForm,
        max_servers: createForm.max_servers ? parseInt(createForm.max_servers) : null,
        max_clusters: createForm.max_clusters ? parseInt(createForm.max_clusters) : null,
      };
      await organizationsAPI.create(payload);
      setShowCreate(false);
      setCreateForm({ name: '', slug: '', description: '', plan: 'free', max_servers: '', max_clusters: '' });
      load();
    } catch (e) {
      setCreateError(e.response?.data?.detail || 'Failed to create organization');
    }
  }

  async function handleEdit(e) {
    e.preventDefault();
    setEditError('');
    try {
      const payload = {
        ...editForm,
        max_servers: editForm.max_servers ? parseInt(editForm.max_servers) : null,
        max_clusters: editForm.max_clusters ? parseInt(editForm.max_clusters) : null,
      };
      await organizationsAPI.update(editOrg.id, payload);
      setEditOrg(null);
      load();
      if (selectedOrg?.id === editOrg.id) selectOrg({ ...editOrg, ...payload });
    } catch (e) {
      setEditError(e.response?.data?.detail || 'Failed to update');
    }
  }

  async function handleDelete(org) {
    if (!window.confirm(`Delete org "${org.name}"? This will delete ALL users, servers, and clusters in this org.`)) return;
    try {
      await organizationsAPI.delete(org.id);
      if (selectedOrg?.id === org.id) setSelectedOrg(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete');
    }
  }

  async function handleCreateUser(e) {
    e.preventDefault();
    setUserError('');
    try {
      await usersAPI.create({ ...userForm, organization_id: selectedOrg.id });
      setShowCreateUser(false);
      setUserForm({ username: '', email: '', password: '', full_name: '', phone_number: '', rbac_role: 'member' });
      selectOrg(selectedOrg);
    } catch (e) {
      setUserError(e.response?.data?.detail || 'Failed to create user');
    }
  }

  function openEditUserPerms(u) {
    setEditUserPerms(u);
    setEditRole(u.rbac_role);
    setEditSectionActions(Object.fromEntries((u.permissions || []).map(p => [p.section, (p.actions && p.actions.length) ? p.actions : ['view','create','edit','delete']])));
    setEditClusters(u.cluster_access_ids || []);
    setEditServers(u.server_access_ids || []);
    setEditUserPermsPassword('');
    setShowEditUserPermsPassword(false);
    setEditPermsError('');
  }

  function toggleUserVerb(section, verb) {
    setEditSectionActions(prev => {
      const cur = prev[section] || [];
      let next = cur.includes(verb) ? cur.filter(v => v !== verb) : [...cur, verb];
      if (next.length && !next.includes('view')) next = ['view', ...next];
      const out = { ...prev };
      if (next.length) out[section] = next; else delete out[section];
      return out;
    });
  }

  async function handleSaveUserPerms() {
    if (!editUserPerms) return;
    setEditPermsLoading(true); setEditPermsError('');
    try {
      const userUpdate = { rbac_role: editRole };
      if (editUserPermsPassword) userUpdate.password = editUserPermsPassword;
      await usersAPI.update(editUserPerms.id, userUpdate);
      await usersAPI.assignPermissions(editUserPerms.id, { section_actions: editSectionActions });
      await usersAPI.assignClusters(editUserPerms.id, editClusters);
      await usersAPI.assignServers(editUserPerms.id, editServers);
      setEditUserPerms(null);
      selectOrg(selectedOrg);
    } catch (e) {
      setEditPermsError(e.response?.data?.detail || 'Failed to save');
    } finally {
      setEditPermsLoading(false);
    }
  }

  async function handleDeleteUser(uid, uname) {
    if (!window.confirm(`Delete user "${uname}"?`)) return;
    try {
      await usersAPI.delete(uid);
      selectOrg(selectedOrg);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed');
    }
  }

  async function handleAssignCluster() {
    if (!assignClusterId || !selectedOrg) return;
    try {
      await clustersAPI.assignOrganization(parseInt(assignClusterId), selectedOrg.id);
      setAssignClusterId('');
      // Refresh the cluster list and org stats
      const cRes = await clustersAPI.list();
      setAllClusters(cRes.data || []);
      selectOrg(selectedOrg);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to assign cluster');
    }
  }

  // Disassociate a cluster from the selected org by moving it to the System org
  // (id 1) — the shared/unassigned pool. This revokes all access grants for it.
  async function handleDisassociateCluster(cluster) {
    if (!selectedOrg) return;
    if (!window.confirm(
      `Remove cluster "${cluster.name}" from ${selectedOrg.name}?\n\n` +
      `It moves to the System (unassigned) pool and all member/group access to it in this org is revoked.`
    )) return;
    try {
      await clustersAPI.assignOrganization(cluster.id, 1);
      const cRes = await clustersAPI.list();
      setAllClusters(cRes.data || []);
      selectOrg(selectedOrg);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to remove cluster from org');
    }
  }

  // Assign a GPU/CPU instance (launched by anyone, e.g. a superadmin who didn't
  // pick an org up front) to the selected org. The org's admins/members then see
  // it under their "My Instances" tab, which is org-scoped for non-superadmins.
  async function handleAssignAllocation() {
    if (!assignAllocId || !selectedOrg) return;
    try {
      await gpuAPI.assignAllocationOrg(parseInt(assignAllocId), selectedOrg.id);
      setAssignAllocId('');
      const aRes = await gpuAPI.allAllocations();
      setAllAllocations(aRes.data || []);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to assign instance');
    }
  }

  // Unassign an instance from the selected org by moving it back to the System
  // org (id 1) — the shared/unassigned pool.
  async function handleUnassignAllocation(alloc) {
    if (!selectedOrg) return;
    if (!window.confirm(
      `Remove instance "${alloc.instance_name}" from ${selectedOrg.name}?\n\n` +
      `It moves back to the System (unassigned) pool.`
    )) return;
    try {
      await gpuAPI.assignAllocationOrg(alloc.id, 1);
      const aRes = await gpuAPI.allAllocations();
      setAllAllocations(aRes.data || []);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to remove instance from org');
    }
  }

  const orgClusters = allClusters.filter(c => c.organization_id === selectedOrg?.id);
  const orgServers  = allServers.filter(s => s.organization_id === selectedOrg?.id);
  // Clusters currently in a DIFFERENT org — candidates to reassign into the selected org
  const assignableClusters = allClusters.filter(c => c.organization_id !== selectedOrg?.id);
  const orgAllocations = allAllocations.filter(a => a.organization_id === selectedOrg?.id);
  // Instances currently in a DIFFERENT org — candidates to reassign into the selected org
  const assignableAllocations = allAllocations.filter(a => a.organization_id !== selectedOrg?.id);

  if (loading) return <div style={{ padding: 32 }}>Loading organizations...</div>;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Organization Management</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: 14 }}>
            Manage orgs, assign users, clusters and servers per org
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn btn-primary">
          + New Organization
        </button>
      </div>

      {error && <div className="banner danger" style={{ marginBottom: 16 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 20 }}>

        {/* Left: Org List */}
        <div>
          {orgs.map(org => (
            <div key={org.id} onClick={() => selectOrg(org)} className="card"
              style={{
                border: `2px solid ${selectedOrg?.id === org.id ? 'var(--brand)' : 'var(--border)'}`,
                marginBottom: 8, cursor: 'pointer',
                boxShadow: selectedOrg?.id === org.id ? '0 0 0 3px rgba(37,99,235,0.1)' : '0 1px 3px rgba(0,0,0,0.05)',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink)' }}>{org.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>/{org.slug}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <PlanBadge plan={org.plan || 'free'} />
                  <span style={{ fontSize: 11, color: org.is_active ? 'var(--color-success-500)' : 'var(--color-danger-500)', fontWeight: 600 }}>
                    {org.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              {org.description && (
                <div style={{ fontSize: 12, color: 'var(--muted-2)', marginTop: 6 }}>{org.description}</div>
              )}
              <div style={{ marginTop: 10 }}>
                <ActionsMenu>
                  <button onClick={() => { setEditOrg(org); setEditForm({ name: org.name, slug: org.slug, description: org.description || '', plan: org.plan || 'free', is_active: org.is_active, max_servers: org.max_servers || '', max_clusters: org.max_clusters || '' }); setEditError(''); }}
                    style={MENU_ITEM}>
                    Edit
                  </button>
                  {org.id !== 1 && (
                    <button onClick={() => handleDelete(org)}
                      style={{ ...MENU_ITEM, color: 'var(--color-danger-500)' }}>
                      Delete
                    </button>
                  )}
                </ActionsMenu>
              </div>
            </div>
          ))}
        </div>

        {/* Right: Org Detail */}
        {selectedOrg ? (
          <div>
            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Users', value: orgStats.user_count ?? orgUsers.length, color: 'var(--brand)' },
                { label: 'Clusters', value: orgStats.cluster_count ?? orgClusters.length, color: 'var(--accent-ink)' },
                { label: 'Servers', value: orgStats.server_count ?? orgServers.length, color: 'var(--color-success-500)' },
              ].map(s => (
                <div key={s.label} className="card" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value ?? '—'}</div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Section Permissions — superadmin allocates CRUD verbs per org */}
            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', marginBottom: 16, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>Section Permissions — Organization Entitlements</div>
                <button onClick={handleSaveOrgSections} disabled={savingSections}
                  style={{ fontSize: 12, padding: '5px 14px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                  {savingSections ? 'Saving…' : 'Save Permissions'}
                </button>
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                Enable a section, then choose which actions this org may perform. Create / Edit / Delete are the permissions an org admin can then use
                (e.g. create GPU/CPU instances on assigned resources). View is always included. Disabled sections are hidden for the org.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                {ALL_SECTIONS.map(s => {
                  const on = orgSections.includes(s);
                  const acts = orgSectionActions[s] || [];
                  return (
                    <div key={s} style={{ padding: '8px 10px', borderRadius: 6, background: on ? '#f5f3ff' : '#f9fafb', border: `1px solid ${on ? '#ddd6fe' : '#e5e7eb'}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, minWidth: 190, fontWeight: 600 }}>
                          <input type="checkbox" checked={on} onChange={() => toggleOrgSection(s)} style={{ accentColor: '#7c3aed', flexShrink: 0 }} />
                          <span>{sectionLabel(s)}</span>
                        </label>
                        <div style={{ display: 'flex', gap: 14, opacity: on ? 1 : 0.35, pointerEvents: on ? 'auto' : 'none' }}>
                          {ACTIONS.map(a => {
                            const checked = a === 'view' ? true : acts.includes(a);
                            return (
                              <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 5, width: 64, cursor: a === 'view' ? 'default' : 'pointer', fontSize: 12, color: '#374151', textTransform: 'capitalize' }}>
                                <input type="checkbox" checked={checked} disabled={a === 'view'} onChange={() => toggleSectionAction(s, a)} style={{ accentColor: '#2563eb', flexShrink: 0 }} />
                                {a}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      {sectionCovers(s) && (
                        <div style={{ fontWeight: 400, fontSize: 10, color: '#9ca3af', marginTop: 4, marginLeft: 26 }}>{sectionCovers(s)}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* GPU Server Access — which registered GPU servers this org may use */}
            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', marginBottom: 16, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>GPU Server Access</div>
                <button onClick={handleSaveGpuServers} disabled={savingGpu}
                  style={{ fontSize: 12, padding: '5px 14px', background: '#6b46c1', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                  {savingGpu ? 'Saving…' : 'Save GPU Access'}
                </button>
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                Select which registered GPU servers this organization can see and provision on. Owned servers are always accessible.
              </div>
              {orgGpuServers.length === 0 ? (
                <div style={{ fontSize: 13, color: '#9ca3af' }}>No GPU servers registered.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                  {orgGpuServers.map(s => {
                    const on = orgGpuAssigned.includes(s.id);
                    return (
                      <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: s.owned ? 'default' : 'pointer', fontSize: 13, padding: '6px 10px', borderRadius: 6, background: on ? '#faf5ff' : '#f9fafb', border: `1px solid ${on ? '#ddd6fe' : '#e5e7eb'}` }}>
                        <input type="checkbox" checked={on} disabled={s.owned} onChange={() => toggleGpuServer(s.id)} style={{ accentColor: '#6b46c1' }} />
                        <span>{s.name} <span style={{ color: '#a0aec0', fontSize: 11 }}>{s.gpu_model || 'GPU'} ×{s.gpu_count}{s.owned ? ' (owned)' : ''}</span></span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* GPU/CPU Instances — assign an already-launched instance to this org.
                Lets a superadmin launch without picking an org up front, then hand
                it to a specific org later; that org's admins/members then see it
                under their My Instances tab (org-scoped for non-superadmins). */}
            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', marginBottom: 16, padding: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>GPU/CPU Instances</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                Assign a launched instance to this org so its admins and members can see it under "My Instances".
              </div>
              {orgAllocations.length === 0 ? (
                <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 10 }}>No instances assigned to this org.</div>
              ) : (
                <div style={{ marginBottom: 10 }}>
                  {orgAllocations.map(a => (
                    <div key={a.id} style={{ padding: '6px 0', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>
                        {a.instance_name} <span style={{ color: '#a0aec0', fontSize: 11 }}>{a.gpu_model || 'CPU'} · {a.username}</span>
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, color: '#6b7280' }}>{a.status}</span>
                        {selectedOrg.id !== 1 && (
                          <button onClick={() => handleUnassignAllocation(a)}
                            title="Remove this instance from the organization"
                            style={{ fontSize: 11, padding: '2px 8px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
                            Remove
                          </button>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Assign an existing instance (from another org) to this org */}
              <div style={{ paddingTop: 12, borderTop: '1px solid #f3f4f6' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Assign an instance to this org</div>
                {assignableAllocations.length === 0 ? (
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>No other instances available to assign</div>
                ) : (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <select value={assignAllocId} onChange={e => setAssignAllocId(e.target.value)}
                      style={{ flex: 1, padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12 }}>
                      <option value="">Select instance…</option>
                      {assignableAllocations.map(a => {
                        const fromOrg = orgs.find(o => o.id === a.organization_id);
                        return <option key={a.id} value={a.id}>{a.instance_name} ({a.username}, from {fromOrg?.name || `org#${a.organization_id}`})</option>;
                      })}
                    </select>
                    <button onClick={handleAssignAllocation} disabled={!assignAllocId}
                      style={{ fontSize: 12, padding: '6px 12px', background: assignAllocId ? '#7c3aed' : '#e5e7eb', color: '#fff', border: 'none', borderRadius: 6, cursor: assignAllocId ? 'pointer' : 'default', fontWeight: 600 }}>
                      Assign
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Users */}
            <div className="table-card" style={{ marginBottom: 16 }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--surface-2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>Users in {selectedOrg.name}</div>
                <button onClick={() => { setShowCreateUser(true); setUserError(''); }}
                  className="btn btn-primary btn-sm">
                  + Add User
                </button>
              </div>
              {orgUsers.length === 0 ? (
                <div style={{ padding: '20px', color: 'var(--muted-2)', textAlign: 'center', fontSize: 13 }}>No users in this organization</div>
              ) : (
                <table className="ds">
                  <thead>
                    <tr>
                      {['Username', 'Email', 'Role', 'Sections', 'Clusters', 'Servers', 'Actions'].map(h => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {orgUsers.map(u => (
                      <tr key={u.id}>
                        <td style={{ fontWeight: 600 }}>{u.username}</td>
                        <td style={{ color: 'var(--muted)', fontSize: 12 }}>{u.email}</td>
                        <td>
                          <span className="badge" style={{ background: u.rbac_role === 'superadmin' ? 'var(--accent)' : u.rbac_role === 'admin' ? 'var(--brand)' : 'var(--muted)', color: '#fff' }}>
                            {u.rbac_role}
                          </span>
                        </td>
                        <td style={{ fontSize: 11, color: 'var(--muted)' }}>
                          {['admin','superadmin'].includes(u.rbac_role)
                            ? <span style={{ color: 'var(--color-success-500)', fontWeight: 600 }}>All</span>
                            : (u.permissions || []).map(p => p.section || p).join(', ') || <span style={{ color: 'var(--muted-2)' }}>None</span>}
                        </td>
                        <td style={{ fontSize: 11, color: 'var(--muted)' }}>
                          {['admin','superadmin'].includes(u.rbac_role)
                            ? <span style={{ color: 'var(--color-success-500)', fontWeight: 600 }}>All</span>
                            : (u.cluster_access_ids || []).length > 0
                              ? `${u.cluster_access_ids.length} cluster(s)`
                              : <span style={{ color: 'var(--muted-2)' }}>None</span>}
                        </td>
                        <td style={{ fontSize: 11, color: 'var(--muted)' }}>
                          {['admin','superadmin'].includes(u.rbac_role)
                            ? <span style={{ color: 'var(--color-success-500)', fontWeight: 600 }}>All</span>
                            : (u.server_access_ids || []).length > 0
                              ? `${u.server_access_ids.length} server(s)`
                              : <span style={{ color: 'var(--muted-2)' }}>None</span>}
                        </td>
                        <td>
                          <ActionsMenu>
                            <button onClick={() => openEditUserPerms(u)} style={MENU_ITEM}>
                              Edit Access
                            </button>
                            <button onClick={() => handleDeleteUser(u.id, u.username)}
                              style={{ ...MENU_ITEM, color: 'var(--color-danger-500)' }}>
                              Remove
                            </button>
                          </ActionsMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Clusters & Servers */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="card">
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Clusters</div>
                {orgClusters.length === 0 ? (
                  <div style={{ color: 'var(--muted-2)', fontSize: 12 }}>No clusters in this org</div>
                ) : orgClusters.map(c => (
                  <div key={c.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--surface-2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>{c.environment}</span>
                      {selectedOrg.id !== 1 && (
                        <button onClick={() => handleDisassociateCluster(c)}
                          title="Remove this cluster from the organization"
                          style={{ fontSize: 11, padding: '2px 8px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
                          Remove
                        </button>
                      )}
                    </span>
                  </div>
                ))}

                {/* Assign an existing cluster (from another org) to this org */}
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f3f4f6' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Assign a cluster to this org</div>
                  {assignableClusters.length === 0 ? (
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>No other clusters available to assign</div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <select value={assignClusterId} onChange={e => setAssignClusterId(e.target.value)}
                        style={{ flex: 1, padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12 }}>
                        <option value="">Select cluster…</option>
                        {assignableClusters.map(c => {
                          const fromOrg = orgs.find(o => o.id === c.organization_id);
                          return <option key={c.id} value={c.id}>{c.name} (from {fromOrg?.name || `org#${c.organization_id}`})</option>;
                        })}
                      </select>
                      <button onClick={handleAssignCluster} disabled={!assignClusterId}
                        style={{ fontSize: 12, padding: '6px 12px', background: assignClusterId ? '#7c3aed' : '#e5e7eb', color: '#fff', border: 'none', borderRadius: 6, cursor: assignClusterId ? 'pointer' : 'default', fontWeight: 600 }}>
                        Assign
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="card">
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Servers</div>
                {orgServers.length === 0 ? (
                  <div style={{ color: 'var(--muted-2)', fontSize: 12 }}>No servers in this org</div>
                ) : orgServers.map(s => (
                  <div key={s.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--surface-2)', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{s.name}</span>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>{s.ip_address}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: 'var(--muted-2)', fontSize: 14, background: 'var(--surface-2)', borderRadius: 10, border: '1px dashed var(--border)' }}>
            Select an organization to view details
          </div>
        )}
      </div>

      {/* Create Org Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 32, width: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ margin: '0 0 20px', fontSize: 18 }}>Create Organization</h2>
            {createError && <div className="banner danger" style={{ marginBottom: 12 }}>{createError}</div>}
            <form onSubmit={handleCreate}>
              {[['name','Name *','text',true],['slug','Slug *','text',true],['description','Description','text',false]].map(([key,label,type,req]) => (
                <div key={key} style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{label}</label>
                  <input type={type} required={req} value={createForm[key]}
                    onChange={e => setCreateForm(p => ({ ...p, [key]: e.target.value }))}
                    className="input" style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
              ))}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Plan</label>
                  <select value={createForm.plan} onChange={e => setCreateForm(p => ({ ...p, plan: e.target.value }))}
                    className="select" style={{ width: '100%' }}>
                    <option value="free">Free</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Max Servers</label>
                  <input type="number" min="0" placeholder="unlimited" value={createForm.max_servers}
                    onChange={e => setCreateForm(p => ({ ...p, max_servers: e.target.value }))}
                    className="input" style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Max Clusters</label>
                  <input type="number" min="0" placeholder="unlimited" value={createForm.max_clusters}
                    onChange={e => setCreateForm(p => ({ ...p, max_clusters: e.target.value }))}
                    className="input" style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowCreate(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Org Modal */}
      {editOrg && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 32, width: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ margin: '0 0 20px', fontSize: 18 }}>Edit: {editOrg.name}</h2>
            {editError && <div className="banner danger" style={{ marginBottom: 12 }}>{editError}</div>}
            <form onSubmit={handleEdit}>
              {[['name','Name *','text'],['slug','Slug *','text'],['description','Description','text']].map(([key,label,type]) => (
                <div key={key} style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{label}</label>
                  <input type={type} value={editForm[key] || ''}
                    onChange={e => setEditForm(p => ({ ...p, [key]: e.target.value }))}
                    className="input" style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
              ))}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Plan</label>
                  <select value={editForm.plan || 'free'} onChange={e => setEditForm(p => ({ ...p, plan: e.target.value }))}
                    className="select" style={{ width: '100%' }}>
                    <option value="free">Free</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Max Servers</label>
                  <input type="number" min="0" value={editForm.max_servers || ''}
                    onChange={e => setEditForm(p => ({ ...p, max_servers: e.target.value }))}
                    className="input" style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Max Clusters</label>
                  <input type="number" min="0" value={editForm.max_clusters || ''}
                    onChange={e => setEditForm(p => ({ ...p, max_clusters: e.target.value }))}
                    className="input" style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                  <input type="checkbox" checked={editForm.is_active !== false} onChange={e => setEditForm(p => ({ ...p, is_active: e.target.checked }))} />
                  Active
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setEditOrg(null)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create User in Org Modal */}
      {showCreateUser && selectedOrg && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 32, width: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Add User</h2>
            <p style={{ margin: '0 0 16px', color: 'var(--muted)', fontSize: 13 }}>Organization: <strong>{selectedOrg.name}</strong></p>
            {userError && <div className="banner danger" style={{ marginBottom: 12 }}>{userError}</div>}
            <form onSubmit={handleCreateUser}>
              {[['username','Username *','text',true],['email','Email *','email',true],['password','Password *','password',true],['full_name','Full Name','text',false],['phone_number','Phone (for 2FA, e.g. 9876543210)','tel',false]].map(([key,label,type,req]) => (
                <div key={key} style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{label}</label>
                  {key === 'password' ? (
                    <div style={{ position: 'relative' }}>
                      <input type={showCreateUserPassword ? 'text' : 'password'} required={req} value={userForm.password}
                        onChange={e => setUserForm(p => ({ ...p, password: e.target.value }))}
                        className="input" style={{ width: '100%', boxSizing: 'border-box', paddingRight: 40 }} />
                      <button type="button" onClick={() => setShowCreateUserPassword(s => !s)}
                        title={showCreateUserPassword ? 'Hide password' : 'Show password'}
                        style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--muted)' }}>
                        {showCreateUserPassword ? '🙈' : '👁'}
                      </button>
                    </div>
                  ) : (
                    <input type={type} required={req} value={userForm[key]}
                      onChange={e => setUserForm(p => ({ ...p, [key]: e.target.value }))}
                      className="input" style={{ width: '100%', boxSizing: 'border-box' }} />
                  )}
                </div>
              ))}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Role</label>
                <select value={userForm.rbac_role} onChange={e => setUserForm(p => ({ ...p, rbac_role: e.target.value }))}
                  className="select" style={{ width: '100%' }}>
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowCreateUser(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary">Add User</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Permissions Modal */}
      {editUserPerms && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 32, width: 580, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18 }}>Edit Access — {editUserPerms.username}</h2>
                <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: 13 }}>{editUserPerms.email} · {selectedOrg?.name}</p>
              </div>
              <button onClick={() => setEditUserPerms(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--muted)' }}>✕</button>
            </div>

            {editPermsError && <div className="banner danger" style={{ marginBottom: 12 }}>{editPermsError}</div>}

            {/* Role */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--ink-2)' }}>Role</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {ROLES.map(r => (
                  <button key={r} onClick={() => setEditRole(r)} style={{
                    padding: '6px 18px', borderRadius: 20, border: '2px solid',
                    borderColor: editRole === r ? 'var(--brand)' : 'var(--border)',
                    background: editRole === r ? 'var(--brand)' : 'var(--surface)',
                    color: editRole === r ? '#fff' : 'var(--ink-2)',
                    cursor: 'pointer', fontWeight: 600, fontSize: 13, textTransform: 'capitalize',
                  }}>{r}</button>
                ))}
              </div>
            </div>

            {/* Password reset */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--ink-2)' }}>
                New Password
                <span style={{ fontWeight: 400, color: 'var(--muted)', marginLeft: 6, fontSize: 12 }}>— leave blank to keep current password</span>
              </label>
              <div style={{ position: 'relative', maxWidth: 280 }}>
                <input
                  type={showEditUserPermsPassword ? 'text' : 'password'}
                  value={editUserPermsPassword}
                  onChange={e => setEditUserPermsPassword(e.target.value)}
                  placeholder="•••••••••"
                  className="input"
                  style={{ width: '100%', boxSizing: 'border-box', paddingRight: 40 }}
                />
                <button type="button" onClick={() => setShowEditUserPermsPassword(s => !s)}
                  title={showEditUserPermsPassword ? 'Hide password' : 'Show password'}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--muted)' }}>
                  {showEditUserPermsPassword ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            {editRole === 'member' && (<>
              {/* Section Permissions */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--ink-2)' }}>
                  Section Permissions — Member Grants
                  <span style={{ fontWeight: 400, color: 'var(--muted)', marginLeft: 6, fontSize: 12 }}>— which sidebar pages this member can access</span>
                </label>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                  Grant per-section actions. You can only grant what your organization is entitled to (greyed = not available to the org). View is implied by any grant.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {ALL_SECTIONS.map(s => {
                    const orgActs = orgSectionActions[s];              // verbs the org allows
                    const cur = editSectionActions[s] || [];
                    const disabled = !orgActs;
                    return (
                      <div key={s} style={{ padding: '8px 10px', borderRadius: 6, background: cur.length ? '#eff6ff' : '#f9fafb', border: `1px solid ${cur.length ? '#bfdbfe' : '#e5e7eb'}`, opacity: disabled ? 0.5 : 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                          <span style={{ minWidth: 190, fontWeight: 600, fontSize: 13 }}>
                            {sectionLabel(s)}{disabled && <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 11 }}> (org n/a)</span>}
                          </span>
                          <div style={{ display: 'flex', gap: 12 }}>
                            {ACTIONS.map(a => {
                              const allowed = !!(orgActs && orgActs.includes(a));
                              return (
                                <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 4, width: 64, fontSize: 12, textTransform: 'capitalize', color: allowed ? '#374151' : '#cbd5e1', cursor: allowed ? 'pointer' : 'not-allowed' }}>
                                  <input type="checkbox" disabled={!allowed} checked={cur.includes(a)} onChange={() => toggleUserVerb(s, a)} style={{ accentColor: '#2563eb', flexShrink: 0 }} />
                                  {a}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                        {sectionCovers(s) && (
                          <div style={{ fontWeight: 400, fontSize: 10, color: '#9ca3af', marginTop: 4 }}>{sectionCovers(s)}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Cluster Access */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--ink-2)' }}>
                  Cluster Access
                  <span style={{ fontWeight: 400, color: 'var(--muted)', marginLeft: 6, fontSize: 12 }}>— none selected = no cluster access</span>
                </label>
                {allClusters.length === 0
                  ? <div style={{ color: 'var(--muted-2)', fontSize: 13 }}>No clusters available</div>
                  : <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {allClusters.map(c => (
                      <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, padding: '7px 12px', borderRadius: 6, background: editClusters.includes(c.id) ? 'var(--color-success-50)' : 'var(--surface-2)', border: `1px solid ${editClusters.includes(c.id) ? 'var(--color-success-500)' : 'var(--border)'}` }}>
                        <input type="checkbox" checked={editClusters.includes(c.id)} onChange={() => setEditClusters(prev => prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id])} style={{ accentColor: 'var(--color-success-500)' }} />
                        <span style={{ fontWeight: 600 }}>{c.name}</span>
                        <span style={{ color: 'var(--muted-2)', fontSize: 11 }}>({c.environment})</span>
                      </label>
                    ))}
                  </div>
                }
              </div>

              {/* Server Access */}
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--ink-2)' }}>
                  Server Access
                  <span style={{ fontWeight: 400, color: 'var(--muted)', marginLeft: 6, fontSize: 12 }}>— none selected = no server access</span>
                </label>
                {allServers.length === 0
                  ? <div style={{ color: 'var(--muted-2)', fontSize: 13 }}>No servers available</div>
                  : <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {allServers.map(s => (
                      <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, padding: '7px 12px', borderRadius: 6, background: editServers.includes(s.id) ? 'var(--accent-soft)' : 'var(--surface-2)', border: `1px solid ${editServers.includes(s.id) ? 'var(--color-accent-100)' : 'var(--border)'}` }}>
                        <input type="checkbox" checked={editServers.includes(s.id)} onChange={() => setEditServers(prev => prev.includes(s.id) ? prev.filter(x => x !== s.id) : [...prev, s.id])} style={{ accentColor: 'var(--accent)' }} />
                        <span style={{ fontWeight: 600 }}>{s.name}</span>
                        <span style={{ color: 'var(--muted-2)', fontSize: 11 }}>{s.ip_address}</span>
                      </label>
                    ))}
                  </div>
                }
              </div>
            </>)}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditUserPerms(null)} className="btn btn-secondary">Cancel</button>
              <button onClick={handleSaveUserPerms} disabled={editPermsLoading} className="btn btn-primary">
                {editPermsLoading ? 'Saving...' : 'Save Access'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
